import React, { useState } from 'react';
import DataImporter from './index';
import { Accordion, Table } from 'react-bootstrap';
import { createWorkOrder } from '../../api/work-orderAPI';
import handleDownloadTemplateHelper from './handleDownloadTemplateHelper';
const WorkOrderImporter = ({ onComplete }) => {
  const [importErrorMessage, setImportErrorMessage] = useState('');
  const assetOrAreaMessages = {
    none: 'Provide either an asset or an area for each work order.',
    multiple: 'Provide only one of asset or area for each work order.',
  };

  const handleDataImport = async (rows, onProgress) => {
    setImportErrorMessage('');
    let count = 0;
    for (const row of rows) {
      try {
        await createWorkOrder({
          extWorkOrderNumber: row?.workOrderNumber || null,
          workOrderTitle: row.workOrderTitle,
          workOrderDescription: row?.workOrderDescription || null,
          priorityId: row?.priorityId || 3, // Open
          statusId: row?.statusId || 1, // Medium
          taskTypeId: row?.taskTypeId || 2, // Preventative
          areaId: row?.areaId || null,
          assetId: row?.assetId || null,
          departmentId: row.departmentId,
          startedAt: row?.startedAt || null,
          completedAt: row?.completedAt || null,
          scheduledAt: row.scheduledAt,
          dueDate: row.dueDate,
          crew: row?.crew || null,
          parentWorkOrderId: row?.parentWorkOrderId || null,
          eventId: row?.eventId || null,
          organisationId: 1,
          teamsId: row?.teamsId || null,
          causeId: row?.causeId || null,
          failureId: row?.failureId || null,
          symptom: row?.symptom || null,
          notes: row?.notes || null,
          taskId: row?.taskId || null,
          componentStartValue: row?.componentStartValue || 0,
          generatedFromDate: row?.generatedFromDate || null,
          sourceType: 'MANUAL',
          statutory: row?.statutory || false,
          cycleGroupOrder: row?.cycleGroupOrder || null,
        });
      } catch (err) {
        const responseMessage = err?.response?.data?.message;
        const isCycleGroupOrderError =
          (Array.isArray(responseMessage) &&
            responseMessage.some((message) =>
              String(message).toLowerCase().includes('cycle group order is required for cycle tasks')
            )) ||
          String(responseMessage || '')
            .toLowerCase()
            .includes('cycle group order is required for cycle tasks');

        if (isCycleGroupOrderError) {
          setImportErrorMessage(
            'Some Work Orders are based on cycle tasks. A valid cycleGroupOrder is requried for these rows'
          );
        }
        console.error('Failed to import work order', err);
      }
      count += 1;
      if (onProgress) onProgress(count);
    }
  };

  return (
    <>
      <div className='card h-100 p-16 rounded-0'>
        <p>
          You can bulk import <strong>work orders</strong> into the system using an Excel or CSV file.
          This helps speed up onboarding or migration processes by avoiding manual entry.
        </p>
        <p>
          Each work order must reference exactly one asset or area. Include one of the
          &nbsp;<code>assetId</code>&nbsp;or&nbsp;<code>areaId</code>&nbsp;columns in every row, but never both.
        </p>
        <p>
          Work order numbers are automatically generated during import using your configured prefix.
          If your source file has a legacy/external work order number, include it in the optional
          <code>workOrderNumber</code> column and it will be stored as the external number.
        </p>
        <p>Expand the lists below to see the required and optional fields.</p>
        <div className='mb-4'>
          <button
            className='btn btn-outline-secondary btn-sm'
            onClick={() => handleDownloadTemplateHelper('workorders')}
          >
            Download Template
          </button>
        </div>
        <div className="mb-4">
          <Accordion alwaysOpen={false}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Required Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file must include the following column headers:</p>
                <p>Note: System work order numbers are auto-generated during import.</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>workOrderTitle</td><td>Text</td><td>Title of the work order</td></tr>
                    <tr><td>assetId</td><td>Lookup</td><td>Asset ID related to the work order</td></tr>
                    <tr><td>areaId</td><td>Lookup</td><td>Area ID related to the work order</td></tr>
                    <tr><td>departmentId</td><td>Lookup</td><td>Department/site ID</td></tr>
                    <tr><td>priorityId</td><td>Lookup</td><td>Priority ID from the system</td></tr>
                    <tr><td>statusId</td><td>Lookup</td><td>Status ID from the system</td></tr>
                    <tr><td>taskTypeId</td><td>Lookup</td><td>Type type ID from the system</td></tr>
                    <tr><td>scheduledAt</td><td>Date</td><td>Scheduled start date</td></tr>
                    <tr><td>dueDate</td><td>Date</td><td>Scheduled end date</td></tr>
                    <tr><td>cycleGroupOrder</td><td>Number</td><td>Required if the work order task belongs to a cycle group</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Optional Columns</Accordion.Header>
              <Accordion.Body>
                <p>Your file can optionally include the following column headers:</p>
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Column Name</th>
                      <th>Type</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>workOrderDescription</td><td>Text</td><td>Description of the work order</td></tr>
                    <tr><td>teamsId</td><td>Lookup</td><td>Assigned team ID</td></tr>
                    <tr><td>crew</td><td>Text</td><td>JSON array of crew member IDs</td></tr>
                    <tr><td>taskId</td><td>Lookup</td><td>Task Type Code associated with the work order</td></tr>
                    <tr><td>startedAt</td><td>Date</td><td>Actual start time</td></tr>
                    <tr><td>completedAt</td><td>Date</td><td>Actual completion time</td></tr>
                    <tr><td>parentWorkOrderId</td><td>Lookup</td><td>Parent work order ID</td></tr>
                    <tr><td>eventId</td><td>Number</td><td>Related event ID</td></tr>
                    <tr><td>notes</td><td>Text</td><td>Completion notes</td></tr>
                    <tr><td>statutory</td><td>Number</td><td>Compliance task flag</td></tr>
                    <tr><td>causeId</td><td>Lookup</td><td>Cause code ID</td></tr>
                    <tr><td>failureId</td><td>Lookup</td><td>Failure code ID</td></tr>
                    <tr><td>symptom</td><td>Lookup</td><td>Failure symptom</td></tr>
                    <tr><td>generatedFromDate</td><td>Date</td><td>Date generated from</td></tr>
                    <tr><td>componentStartValue</td><td>Number</td><td>Meter value when work order started</td></tr>
                    <tr><td>workOrderNumber</td><td>Text</td><td>Optional external/legacy work order number</td></tr>
                  </tbody>
                </Table>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <DataImporter
          entity="workorders"
          requiredFields={[
            { key: 'workOrderTitle', type: 'string', required: true },
            { key: 'priorityId', type: 'lookup', required: true, api: 'priority', nameField: 'name' },
            { key: 'statusId', type: 'lookup', required: true, api: 'work-order-status', nameField: 'name' },
            { key: 'taskTypeId', type: 'lookup', required: true, api: 'task-types', nameField: 'taskTypeCode' },
            { key: 'departmentId', type: 'lookup', required: true, api: 'hierarchy-structures', nameField: 'name' },
            {
              key: 'areaId',
              type: 'lookup',
              api: 'areas',
              nameField: 'name',
              xorGroup: 'assetOrArea',
              xorMessages: assetOrAreaMessages,
            },
            {
              key: 'assetId',
              type: 'lookup',
              api: 'assets',
              nameField: 'assetNumber',
              xorGroup: 'assetOrArea',
              xorMessages: assetOrAreaMessages,
            },
            { key: 'scheduledAt', type: 'datetime', required: false },
            { key: 'dueDate', type: 'datetime', required: false },
            { key: 'teamsId', type: 'lookup', api: 'teams', nameField: 'teamName' },
            { key: 'causeId', type: 'lookup', api: 'cause-codes', nameField: 'name' },
            { key: 'failureId', type: 'lookup', api: 'failure-codes', nameField: 'name' },
            { key: 'taskId', type: 'lookup', api: 'tasks', nameField: 'name' },
            { key: 'componentStartValue', type: 'number' },
            { key: 'generatedFromDate', type: 'date' },
            { key: 'sourceType', type: 'string' },
            { key: 'statutory', type: 'number' },
            { key: 'workOrderDescription', type: 'string' },
            { key: 'startedAt', type: 'datetime' },
            { key: 'completedAt', type: 'datetime' },
            { key: 'crew', type: 'string' },
            { key: 'parentWorkOrderId', type: 'lookup', api: 'work-orders', nameField: 'workOrderNumber' },
            { key: 'eventId', type: 'number' },
            { key: 'organisationId', type: 'number' },
            { key: 'notes', type: 'string' },
            { key: 'cycleGroupOrder', type: 'number' },
            { key: 'workOrderNumber', type: 'string' },
          ]}
          onCommit={handleDataImport}
          onComplete={onComplete}
          importErrorMessage={importErrorMessage}
        />
      </div>
    </>
  );
};

export default WorkOrderImporter;
